import React, { useEffect, useState } from "react";
import Table from "../../../core/pagination/datatable";
import { Link } from "react-router-dom";
import AddPrinter from "./AddPrinter";
import { Spin, Modal, Input } from "antd";
import Swal from "sweetalert2";
import EditPrinter from "./EditPrinter";
import {
  DELETE_PRINTER,
  GET_PRINTERS,
  getApiBaseUrl,
  ADD_ACTIVITY,
  LOGIN,
} from "../Config/Config";

const Printers = () => {
  const [loading, setLoading] = useState(true);
  const [printerData, setPrinterData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reauthVisible, setReauthVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const fetchPrinterData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}${GET_PRINTERS}`);
      const data = await res.json();
      const result = data?.ResultData || [];
      setPrinterData(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrinterData();
  }, []);

  const logActivity = async (isSuccess, note) => {
    try {
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ActivityType: 1,
          PerformedBy: user.UserID || 0,
          PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
          Notes: note,
          Location: "deletePrinter",
          IsSucces: isSuccess ? 1 : 0,
        }),
      });
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    setDeleteLoading(true);
    setPasswordError("");
    const username = user?.UserName || user?.Username;

    try {
      const loginRes = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const loginData = await loginRes.json();

      if (loginData?.Message === "Login successful") {
        setReauthVisible(false);
        setPassword("");
        await handleDeleteConfirmed(selectedRecord);
      } else {
        setPasswordError(loginData.Message);
        // await logActivity(
        //   0,
        //   `Failed reauthentication for deleting Printer "${selectedRecord?.name}" by ${username}`
        // );
      }
    } catch (error) {
      console.error("Reauthentication error:", error);
      setPasswordError("Something went wrong. Please try again.");
      await logActivity(
        0,
        `Reauthentication error for deleting Printer "${selectedRecord?.name}" by ${username}`
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirmed = async (record) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete printer: ${record.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      setDeleteLoading(true);
      const response = await fetch(`${getApiBaseUrl()}${DELETE_PRINTER}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: record.id,
          updatedby: user.UserID || 1,
        }),
      });

      const data = await response.json();
      const result = data?.ResultData?.[0];

      if (!data?.Status || result?.Status !== "Success") {
        throw new Error(result?.ResultMessage || "Delete failed");
      }

      setPrinterData((prev) => prev.filter((item) => item.id !== record.id));

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: `Printer "${record.name}" deleted successfully`,
        timer: 1500,
        showConfirmButton: false,
      });
      await logActivity(1, `Deleted Printer "${record.name}"`);
      fetchPrinterData();
    } catch (error) {
      console.error("Error deleting printer:", error);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Something went wrong.",
      });
      await logActivity(0, `Failed to delete Printer "${record.name}"`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDelete = (record) => {
    setSelectedRecord(record);
    setReauthVisible(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "IP Address",
      dataIndex: "ipadress",
      key: "ipadress",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div className="action-table-data">
          <div className="edit-delete-action d-flex align-items-center">
            <Link
              to="#"
              className="me-2"
              data-bs-toggle="offcanvas"
              data-bs-target="#EditPrinterOffcanvas"
              aria-controls="EditPrinterOffcanvas"
              onClick={() => setSelectedRecord(record)}
              title="Edit"
            >
              <i data-feather="edit" className="feather-edit text-info"></i>
            </Link>
            <Link
              className="me-2"
              onClick={() => handleDelete(record)}
              title="Delete"
            >
              <i data-feather="trash" className="feather-trash text-danger"></i>
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {deleteLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)", // subtle tint
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/assetmgt/dashboard">
                      <i className="fas fa-home fa-lg" />
                    </Link>
                  </li>
                  <h4 className="breadcrumb-item " aria-current="page">
                    Printer List
                  </h4>
                </ol>
              </nav>
              <h6>Manage Printers</h6>
            </div>

            <div className="page-btn">
              <Link
                data-bs-toggle="offcanvas"
                data-bs-target="#AddPrinterOffcanvas"
                aria-controls="AddPrinterOffcanvas"
                className="btn btn-success btn-sm"
              >
                <i className="ti ti-circle-plus fs-4"></i>
              </Link>
            </div>
          </div>
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3  ">
              <div className="search-set mt-4"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3"></div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center my-4">
                    <Spin size="large" />
                    <p>Loading data...</p>
                  </div>
                ) : printerData?.length > 0 ? (
                  <Table
                    columns={columns}
                    dataSource={printerData}
                    rowKey="id"
                  />
                ) : (
                  <p className="text-center my-4">No data available</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <AddPrinter />
        <EditPrinter printerObj={selectedRecord} />
        <Modal
          title="Confirm Password"
          open={reauthVisible}
          onOk={handlePasswordSubmit}
          onCancel={() => {
            setReauthVisible(false);
            setPassword("");
            setPasswordError("");
            setSelectedRecord(null);
          }}
          confirmLoading={deleteLoading}
          width={350}
        >
          <Spin spinning={deleteLoading}>
            <Input.Password
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />
            {passwordError && (
              <div className="text-danger mt-2">{passwordError}</div>
            )}
          </Spin>
        </Modal>
      </div>
    </>
  );
};

export default Printers;
